import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import LatestPatchNotesCard from '../components/LatestPatchNotesCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import AreaCombobox from '../components/AreaCombobox';
import { toast } from 'sonner';
import { formatRomeDateTime } from '../lib/datetime';
import {
  Droplet,
  Wrench,
  Zap,
  Lock,
  Thermometer,
  Wifi,
  Sofa,
  Tv,
  Brush,
  Palette,
  DoorOpen,
  HelpCircle,
  Send,
  Loader2,
  CheckCircle2,
  Building2,
  Ticket as TicketIcon,
  ChevronRight,
  Clock
} from 'lucide-react';

const issueTypes = [
  { id: 'lampada',    label: 'Accessori Bagno',     hint: 'rubinetti, sifoni, doccia, saliscendi',  icon: Droplet,     color: 'bg-blue-100 text-blue-700' },
  { id: 'acqua',      label: 'Idraulico',           hint: 'WC, perdite, scarichi, infiltrazioni',   icon: Wrench,      color: 'bg-sky-100 text-sky-700' },
  { id: 'elettrico',  label: 'Elettrico',           hint: 'prese, luci, interruttori, lampadari',   icon: Zap,         color: 'bg-yellow-100 text-yellow-700' },
  { id: 'serratura',  label: 'Serratura',           hint: 'chiavi, porte, lock card',               icon: Lock,        color: 'bg-slate-100 text-slate-700' },
  { id: 'clima',      label: 'Clima',               hint: 'aria, riscaldamento, termostato',        icon: Thermometer, color: 'bg-cyan-100 text-cyan-700' },
  { id: 'wifi',       label: 'TV/WiFi',             hint: 'televisione, telecomando, internet',     icon: Wifi,        color: 'bg-purple-100 text-purple-700' },
  { id: 'arredo',     label: 'Arredamento',         hint: 'armadi, sedie, tavoli, ante, zoccolino', icon: Sofa,        color: 'bg-orange-100 text-orange-700' },
  // Nuove categorie operative (02/2026)
  { id: 'dispositivi',label: 'Dispositivi camera',  hint: 'minibar, cassaforte, telefoni',          icon: Tv,          color: 'bg-pink-100 text-pink-700' },
  { id: 'silicone',   label: 'Siliconature',        hint: 'giunture, bagno, doccia, sigillature',   icon: Brush,       color: 'bg-lime-100 text-lime-700' },
  { id: 'pittura',    label: 'Pitture',             hint: 'pareti, ritocchi, scrostature',          icon: Palette,     color: 'bg-rose-100 text-rose-700' },
  { id: 'serramenti', label: 'Serramenti',          hint: 'finestre, persiane, porte balcone',      icon: DoorOpen,    color: 'bg-violet-100 text-violet-700' },
  // "Altro" è volutamente l'ultima opzione, con stile neutro per disincentivare la scelta
  // quando esiste una categoria specifica più adatta.
  { id: 'altro',      label: 'Altro',               hint: 'solo se nessuna delle precedenti',       icon: HelpCircle,  color: 'bg-gray-100 text-gray-500', isOther: true }
];

const HousekeepingDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState('');
  const [room, setRoom] = useState('');
  const [selectedIssue, setSelectedIssue] = useState('');
  const [isOccupied, setIsOccupied] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [recentTickets, setRecentTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [hotelAreas, setHotelAreas] = useState({ rooms: [], common_areas: [], all_areas: [] });

  const fetchHotelAreas = async (hotelId) => {
    if (!hotelId) {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
      return;
    }
    try {
      const { data } = await api.get(`/hotels/${hotelId}/areas`);
      setHotelAreas(data);
    } catch {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
    }
  };

  const loadRecentTickets = async () => {
    try {
      setTicketsLoading(true);
      // Carichiamo solo gli ultimi 7 giorni e limitiamo a 50 ticket (poi filtriamo a 24h client side)
      const from = new Date();
      from.setDate(from.getDate() - 7);
      const { data } = await api.get(`/tickets?limit=50&from_date=${from.toISOString()}`);
      const items = Array.isArray(data) ? data : (data.items || []);
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      const recent = items
        .filter(t => new Date(t.created_at).getTime() >= cutoff)
        .slice(0, 20);
      setRecentTickets(recent);
    } catch (e) {
      // silenzioso
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [hotelsRes, catsRes] = await Promise.all([
          api.get('/hotels'),
          api.get('/categories')
        ]);
        setHotels(hotelsRes.data);
        setCategories(catsRes.data);
        
        // Auto-select first hotel if user has assigned hotels
        if (hotelsRes.data.length > 0) {
          const firstHotelId = hotelsRes.data[0].id;
          setSelectedHotel(firstHotelId);
          fetchHotelAreas(firstHotelId);
        }
      } catch (err) {
        toast.error('Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    loadRecentTickets();
  }, []);

  const getCategoryId = (issueType) => {
    // Mapping con fallback intelligente: per le categorie storiche che hanno avuto
    // due nomi in ambienti diversi (Arredamento/Falegnameria, Pulizia/Pulizie),
    // cerchiamo entrambe le varianti così funziona sia in produzione che in preview.
    const mapping = {
      lampada:     ['Accessori Bagno'],
      acqua:       ['Idraulica'],
      elettrico:   ['Elettrico'],
      serratura:   ['Serrature'],
      clima:       ['HVAC'],
      wifi:        ['TV/WiFi'],
      arredo:      ['Arredamento', 'Falegnameria'],     // prova prima la storica
      // Nuove categorie 02/2026
      dispositivi: ['Dispositivi camera'],
      silicone:    ['Siliconature'],
      pittura:     ['Pitture'],
      serramenti:  ['Serramenti'],
      altro:       ['Altro'],
    };
    const candidates = mapping[issueType] || [];
    for (const name of candidates) {
      const c = categories.find(x => x.name === name);
      if (c) return c.id;
    }
    // Fallback finale: Altro (esiste sia in prod che preview)
    return categories.find(c => c.name === 'Altro')?.id || '';
  };

  const handleSubmit = async () => {
    if (submitting) return; // Guard contro doppio-click / rapid fire submit
    if (!selectedHotel || !room || !selectedIssue) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    // Per "Altro" la descrizione è obbligatoria (min 5 char) per evitare ticket muti nelle statistiche
    if (selectedIssue === 'altro' && description.trim().length < 5) {
      toast.error('Hai scelto "Altro": descrivi il problema (minimo 5 caratteri).');
      return;
    }

    try {
      setSubmitting(true);
      const issueLabel = issueTypes.find(i => i.id === selectedIssue)?.label || selectedIssue;
      
      await api.post('/quick-report', {
        hotel_id: selectedHotel,
        room: room,
        issue_type: issueLabel,
        category_id: getCategoryId(selectedIssue),
        description: description || `Segnalazione: ${issueLabel} - Camera ${room}`,
        is_occupied: isOccupied,
        urgent: isUrgent
      });

      setSuccess(true);
      toast.success('Segnalazione inviata!');
      // Aggiorna lista ticket recenti per mostrare anche quello appena creato
      loadRecentTickets();
      
      // Reset form after delay
      setTimeout(() => {
        setRoom('');
        setSelectedIssue('');
        setIsOccupied(false);
        setIsUrgent(false);
        setDescription('');
        setSuccess(false);
      }, 2000);
    } catch (err) {
      toast.error('Errore nell\'invio della segnalazione');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="success-screen">
        <div className="text-center animate-fade-in">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-green-600" />
          </div>
          <h2 className="text-2xl font-black text-slate-900 mb-2">Segnalazione Inviata!</h2>
          <p className="text-slate-500">Il team manutenzione è stato avvisato</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="housekeeping-dashboard">
      {/* Header */}
      <div className="text-center pt-4">
        <h1 className="text-3xl font-black text-slate-900">Segnalazione Rapida</h1>
        <p className="text-slate-500 mt-2">Ciao {user?.name}, segnala un problema</p>
      </div>

      {/* Hotel selection */}
      <Card className="border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-blue-900" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-slate-700">Hotel</label>
              <Select value={selectedHotel} onValueChange={(v) => { setSelectedHotel(v); setRoom(''); fetchHotelAreas(v); }}>
                <SelectTrigger className="mt-1" data-testid="hotel-select">
                  <SelectValue placeholder="Seleziona hotel" />
                </SelectTrigger>
                <SelectContent>
                  {hotels.map(h => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Room input */}
          <div>
            <label className="text-sm font-medium text-slate-700">Camera / Area *</label>
            <AreaCombobox
              value={room}
              onChange={setRoom}
              rooms={hotelAreas.rooms || []}
              commonAreas={hotelAreas.common_areas || []}
              disabled={!selectedHotel}
              placeholder={!selectedHotel ? "Seleziona prima l'hotel" : 'es. 301, Hall, Ristorante'}
              className="mt-1 h-14 text-lg"
              testId="hk-area-combobox"
              inputTestId="room-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Issue type selection - Bento grid */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold">Tipo di Problema *</CardTitle>
          <p className="text-xs text-slate-500 mt-1">Scegli la categoria più precisa: aiuta a smistare il ticket al tecnico giusto.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {issueTypes.map((issue) => {
              const Icon = issue.icon;
              const isSelected = selectedIssue === issue.id;
              const isOther = issue.isOther;
              return (
                <button
                  key={issue.id}
                  onClick={() => setSelectedIssue(issue.id)}
                  className={`bento-button ${isSelected ? 'selected' : ''} ${isOther ? 'opacity-80 hover:opacity-100 col-span-2 md:col-span-1' : ''}`}
                  data-testid={`issue-${issue.id}`}
                  title={issue.hint}
                >
                  <div className={`w-14 h-14 rounded-2xl ${issue.color} flex items-center justify-center`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{issue.label}</span>
                  {issue.hint && (
                    <span className="text-[10px] text-slate-500 dark:text-slate-400 text-center leading-tight px-1 mt-0.5">
                      {issue.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {selectedIssue === 'altro' && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg" data-testid="altro-hint">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Hai scelto &quot;Altro&quot;.</strong> Per non disperdere il ticket nelle statistiche, <b>compila la descrizione qui sotto</b> con dettagli (minimo 5 caratteri).
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Options */}
      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-4">
          {/* Occupied toggle */}
          <div
            onClick={() => setIsOccupied(!isOccupied)}
            className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
              isOccupied ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid="occupied-toggle"
          >
            <span className="font-medium text-slate-700">Camera Occupata</span>
            <div className={`w-12 h-7 rounded-full transition-colors ${isOccupied ? 'bg-amber-500' : 'bg-slate-200'}`}>
              <div className={`w-5 h-5 bg-white rounded-full mt-1 transition-transform ${isOccupied ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
          </div>

          {/* Urgent toggle */}
          <div
            onClick={() => setIsUrgent(!isUrgent)}
            className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
              isUrgent ? 'border-red-500 bg-red-50' : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid="urgent-toggle"
          >
            <span className="font-medium text-slate-700">Urgente</span>
            <div className={`w-12 h-7 rounded-full transition-colors ${isUrgent ? 'bg-red-500' : 'bg-slate-200'}`}>
              <div className={`w-5 h-5 bg-white rounded-full mt-1 transition-transform ${isUrgent ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
          </div>

          {/* Description (required only when "Altro" is selected) */}
          <div>
            <label className="text-sm font-medium text-slate-700">
              {selectedIssue === 'altro' ? (
                <>Descrizione del problema <span className="text-red-500">*</span></>
              ) : (
                <>Note aggiuntive (opzionale)</>
              )}
            </label>
            <Input
              placeholder={selectedIssue === 'altro' ? 'Descrivi il problema (minimo 5 caratteri)...' : 'Dettagli aggiuntivi...'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={`mt-1 ${selectedIssue === 'altro' && description.trim().length > 0 && description.trim().length < 5 ? 'border-red-400 focus-visible:ring-red-400' : ''}`}
              data-testid="description-input"
              minLength={selectedIssue === 'altro' ? 5 : 0}
              maxLength={500}
            />
            {selectedIssue === 'altro' && description.trim().length > 0 && description.trim().length < 5 && (
              <p className="text-xs text-red-500 mt-1">Descrivi con almeno 5 caratteri.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Submit button */}
      <Button
        onClick={handleSubmit}
        disabled={
          submitting ||
          !selectedHotel ||
          !room ||
          !selectedIssue ||
          (selectedIssue === 'altro' && description.trim().length < 5)
        }
        className="w-full h-16 text-lg font-bold bg-blue-900 hover:bg-blue-800"
        data-testid="submit-report"
      >
        {submitting ? (
          <Loader2 className="w-6 h-6 animate-spin" />
        ) : (
          <>
            <Send className="w-6 h-6 mr-2" />
            Invia Segnalazione
          </>
        )}
      </Button>

      {/* Ticket aperti nelle ultime 24 ore */}
      <Card className="border-slate-200" data-testid="recent-tickets-card">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2 text-slate-900">
            <TicketIcon className="w-4 h-4 text-blue-700" />
            Ticket aperti nelle ultime 24 ore
            {!ticketsLoading && (
              <span className="ml-1 text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {recentTickets.length}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {ticketsLoading ? (
            <div className="space-y-2">
              <div className="h-14 bg-slate-100 rounded animate-pulse" />
              <div className="h-14 bg-slate-100 rounded animate-pulse" />
            </div>
          ) : recentTickets.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-500" data-testid="no-recent-tickets">
              Nessun ticket aperto nelle ultime 24 ore.
            </div>
          ) : (
            recentTickets.map((t) => {
              const prio = t.priority || 'media';
              const prioColors = {
                urgente: 'bg-red-100 text-red-800 border-red-200',
                alta: 'bg-orange-100 text-orange-800 border-orange-200',
                media: 'bg-blue-100 text-blue-800 border-blue-200',
                bassa: 'bg-slate-100 text-slate-700 border-slate-200'
              };
              const statusLabels = {
                aperto: 'Aperto',
                assegnato: 'Assegnato',
                in_lavorazione: 'In lavorazione',
                in_attesa_pezzi: 'In attesa pezzi',
                completato: 'Completato',
                chiuso: 'Chiuso'
              };
              const statusColors = {
                aperto: 'bg-amber-50 text-amber-700',
                assegnato: 'bg-blue-50 text-blue-700',
                in_lavorazione: 'bg-indigo-50 text-indigo-700',
                in_attesa_pezzi: 'bg-purple-50 text-purple-700',
                completato: 'bg-emerald-50 text-emerald-700',
                chiuso: 'bg-slate-100 text-slate-600'
              };
              const hotelName = hotels.find(h => h.id === t.hotel_id)?.name || '';
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => navigate(`/tickets/${t.id}`)}
                  className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                  data-testid={`recent-ticket-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${prioColors[prio]}`}>
                        {prio}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${statusColors[t.status] || 'bg-slate-100 text-slate-600'}`}>
                        {statusLabels[t.status] || t.status}
                      </span>
                      {t.assigned_to_name && (
                        <span className="text-[10px] text-slate-500">· {t.assigned_to_name}</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-blue-700">{t.title}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      {hotelName && <span>{hotelName} ·</span>}
                      <span className="truncate">{t.area}</span>
                      <span>·</span>
                      <Clock className="w-3 h-3" />
                      <span>{formatRomeDateTime(t.created_at)}</span>
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 shrink-0" />
                </button>
              );
            })
          )}
        </CardContent>
      </Card>

      <LatestPatchNotesCard limit={3} />
    </div>
  );
};

export default HousekeepingDashboard;
